import { EnrichedColumn } from '../schemas/EnrichedColumn.js';
import { camelToSnakeCase, pascalCaseToLabel, pascalToSnakeCase } from '../utils/strings.js';
/** Use the same model-prefixed endpoints from either side of a many-to-many relation. */
export function getPivotTableName(modelName, relation) {
    const name = [
        `${pascalToSnakeCase(modelName)}_${camelToSnakeCase(relation.name)}`,
        `${pascalToSnakeCase(relation.model)}_${camelToSnakeCase(relation.inverse)}`,
    ]
        .sort()
        .join('_');
    return shortenPivotIdentifier(name);
}
/** The parent's ID is stored under the inverse relation's name. */
export function getPivotForeignKey(relation) {
    return shortenPivotIdentifier(`${pascalToSnakeCase(relation.model)}_${camelToSnakeCase(relation.inverse)}`, '_id');
}
/** The related model's ID is stored under this relation's name. */
export function getPivotRelatedKey(modelName, relation) {
    return shortenPivotIdentifier(`${pascalToSnakeCase(modelName)}_${camelToSnakeCase(relation.name)}`, '_id');
}
/** MySQL identifiers are limited to 64 characters. FNV-1a works in the browser and Node. */
export function shortenPivotIdentifier(identifier, suffix = '') {
    const name = identifier + suffix;
    if (name.length <= 64)
        return name;
    let hash = 0xcbf29ce484222325n;
    for (let index = 0; index < name.length; index++) {
        hash ^= BigInt(name.charCodeAt(index));
        hash = BigInt.asUintN(64, hash * 0x100000001b3n);
    }
    return `${identifier.slice(0, 47 - suffix.length)}_${hash.toString(16).padStart(16, '0')}${suffix}`;
}
// Relation type guards
export function isBelongsToRelation(relation) {
    return relation.relationType === 'BelongsTo';
}
export function isHasManyRelation(relation) {
    return relation.relationType === 'HasMany';
}
export function isHasOneRelation(relation) {
    return relation.relationType === 'HasOne';
}
export function isBelongsToManyRelation(relation) {
    return relation.relationType === 'BelongsToMany';
}
// Relation type assertions
export function assertBelongsToRelation(relation) {
    if (!isBelongsToRelation(relation)) {
        throw new Error(`Relation is not a BelongsTo relation`);
    }
}
export function assertHasManyRelation(relation) {
    if (!isHasManyRelation(relation)) {
        throw new Error(`Relation is not a HasMany relation`);
    }
}
export function assertHasOneRelation(relation) {
    if (!isHasOneRelation(relation)) {
        throw new Error(`Relation is not a HasOne relation`);
    }
}
export function assertBelongsToManyRelation(relation) {
    if (!isBelongsToManyRelation(relation)) {
        throw new Error(`Relation is not a BelongsToMany relation`);
    }
}
export function isForeignIdForRelation(column) {
    return column.type === 'foreignId' && 'forRelation' in column && column.forRelation !== '';
}
/**
 * Creates a foreign key column for a belongsTo relation
 */
export function makeForeignKeyForBelongsToRelation(relation, module, order) {
    if (!module.model.relations[relation.name]) {
        throw new Error(`Provided relation ${relation.name} not found in provided module ${module.name}`);
    }
    return new EnrichedColumn({
        name: pascalToSnakeCase(relation.model) + '_id',
        displayName: pascalCaseToLabel(relation.model + 'Id'),
        displayNamePlural: pascalCaseToLabel(relation.model + 'Ids'),
        type: 'foreignId',
        typeConstraints: [
            {
                name: 'nullable',
                type: 'modifier',
                params: [],
            },
        ],
        forRelation: relation.name,
        order,
    });
}
/**
 * Creates a foreign key column for the inverse belongsTo relation of a hasMany relation
 */
export function makeForeignKeyForHasManyRelation(relation, inverseModule, order) {
    if (relation.model !== inverseModule.name) {
        throw new Error(`Relation model ${relation.model} does not match inverse module name ${inverseModule.name}`);
    }
    const inverseRelation = inverseModule.model.relations[relation.inverse];
    assertBelongsToRelation(inverseRelation);
    return makeForeignKeyForBelongsToRelation(inverseRelation, inverseModule, order);
}
/**
 * Creates a foreign key column for the inverse belongsTo relation of a hasOne relation
 */
export function makeForeignKeyForHasOneRelation(relation, inverseModule, order) {
    if (relation.model !== inverseModule.name) {
        throw new Error(`Relation model ${relation.model} does not match inverse module name ${inverseModule.name}`);
    }
    const inverseRelation = inverseModule.model.relations[relation.inverse];
    assertBelongsToRelation(inverseRelation);
    return makeForeignKeyForBelongsToRelation(inverseRelation, inverseModule, order);
}
